/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  DEFAULT_APPLICATION_NAME,
  DEFAULT_DEVICE_TYPE,
  DEFAULT_MSO_NAME,
  SUPPORTED_DEVICE_TYPES
} from "../../config";
import { DeviceType, MessageType } from "../enums";
import { AppError } from "../error";
import { MessageService } from "../infrastructure";
import { Log } from "../logging";
import {
  StorageAdapter,
  STORAGE_KEY_BODY_ID,
  STORAGE_KEY_PARTNER_ID,
  STORAGE_KEY_PARTNER_CUSTOMER_ID,
  STORAGE_KEY_OVERRIDE_DEVICE_TYPE
} from "../storage";

/**
 * MSO override configuration for player and closed caption settings.
 */
interface IMsoOverride {
  closedCaption608Allowed?: boolean;
  closedCaption708Allowed?: boolean;
  playerMsoConfig?: Record<string, unknown>;
  subtitleSmpteAllowed?: boolean;
}

/**
 * TiVo Settings received from shell app and config file.
 */
interface ITivoSettings {
  appName?: string;
  browserAllowed?: boolean;
  deviceType?: string;
  msoName?: string;
  msoOverride?: IMsoOverride;
  msoRegionName?: string;
  provisioningDomain?: string;
  segmentKey?: string;
  shellVersion?: string;
  // Keys of the externally-hosted MSO config JSON. They keep their `sls`
  // names because renaming them here would stop the remote config parsing —
  // the object is spread straight from the fetched JSON.
  slsDomain?: string;
  slsEndpointsUrl?: string;
  webAppUrl?: string;
}

/**
 * Extended Window with shell app injections.
 */
interface IWindowExtended extends Window {
  appInfo?: Record<string, unknown>;
  shellAppVersion?: string;
}

export class TivoSettings {
  private static readonly TAG: string = "TivoSettings";

  private oTivoSettings: ITivoSettings = {};

  private mIsValidMsoConfig: boolean = false;

  private mProfileId: string = "";

  /** Matches gaming console user agents. */
  private static readonly CONSOLE_REGEX = /Xbox|PlayStation/i;

  /** Matches Android TV devices. */
  private static readonly ANDROID_TV_REGEX = /Android TV/i;

  /**
   * Matches Smart TV and streaming platform user agents.
   * Amazon Vega OS (Fire TV WebView) reports `Kepler <ver>` plus an
   * `AFT*` model code, alongside `Mobile` — so it must be checked before
   * MOBILE_REGEX.
   */
  private static readonly SMART_TV_REGEX =
    /SmartTV|smart-tv|smarttv|HbbTV|NetCast|Web0S|webOS|Tizen|GoogleTV|AppleTV|CrKey|Roku|Kepler|\bAFT[A-Z0-9]+|PhilipsTV|BRAVIA|VESTEL|pov_tv/i;

  /** Matches mobile phone user agents. */
  private static readonly MOBILE_REGEX =
    /Android.*Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone|KaiOS|Symbian|Series[46]0|Palm|J2ME|MIDP|BREW/i;

  /** Matches tablet user agents. */
  private static readonly TABLET_REGEX =
    /iPad|Android(?!.*Mobile)|Tablet|Kindle|Silk(?!.*Mobile)|PlayBook/i;

  /** Matches macOS devices. Used for iPadOS detection. */
  private static readonly MACINTOSH_REGEX = /Macintosh/i;

  private async fetchAppConfig(
    resolve: (value: boolean) => void,
    reject: (reason?: unknown) => void,
    overriddenTsriEnvironment?: string
  ): Promise<void> {
    try {
      // URL-driven config resolution (Unity Shell HLD strategy)
      let configFileName: string;
      const urlDerivedConfigName = this.getConfigFileNameFromUrl();

      // Fallback: msoName-based configuration (backward compatibility for existing shells)
      configFileName = `${urlDerivedConfigName || this.getMsoName()}`;
      if (overriddenTsriEnvironment) {
        configFileName += `-${overriddenTsriEnvironment}`;
      }
      configFileName += `-config.json`;

      const configFileURL = `https://d2sj8cz5agvefv.cloudfront.net/${configFileName}`;
      let response: Response;

      try {
        response = await fetch(configFileURL);
      } catch {
        throw new AppError(
          "CONFIG_FETCH_FAILED",
          "Failed to fetch config: " + configFileName
        );
      }

      const configs = await response.json();
      let msoName: string = this.getMsoName();
      if (!Object.keys(configs).length)
        throw new AppError("EMPTY_CONFIG", "Empty config found");

      if (!msoName) {
        const [firstMsoName] = Object.keys(configs);
        msoName = firstMsoName;
      }
      const appConfiguration = configs[msoName];
      this.oTivoSettings = { ...this.oTivoSettings, ...appConfiguration };
      const neededConfigKeys = [
        "msoName",
        "deviceType",
        "segmentKey",
        "slsEndpointsUrl",
        "slsDomain"
      ];
      const appConfigurationKeys = Object.keys(appConfiguration);
      this.mIsValidMsoConfig = neededConfigKeys.every((key) =>
        appConfigurationKeys.includes(key)
      );
      resolve(true);
    } catch (error) {
      Log.e(
        TivoSettings.TAG,
        "fetchAppConfig(): Failed to get app config.",
        error
      );
      reject(error);
    }
  }

  public getProfileId = (): string => {
    return this.mProfileId;
  };

  public setProfileId = (profileId: string): void => {
    this.mProfileId = profileId;
  };

  /**
   * Detects the current device category based on the browser User-Agent
   * and available platform capabilities.
   *
   * Detection priority:
   * 1. Gaming Consoles
   * 2. Smart TVs / TV platforms
   * 3. Mobile phones
   * 4. Tablets
   * 5. Touch-enabled iPadOS devices reporting as Macintosh
   * 6. Desktop
   *
   * @returns The detected device type.
   */
  public detectDeviceType(): DeviceType {
    const ua = navigator.userAgent;

    if (TivoSettings.CONSOLE_REGEX.test(ua)) {
      return DeviceType.Console;
    }

    if (
      TivoSettings.ANDROID_TV_REGEX.test(ua) ||
      TivoSettings.SMART_TV_REGEX.test(ua)
    ) {
      return DeviceType.TV;
    }

    if (TivoSettings.MOBILE_REGEX.test(ua)) {
      return DeviceType.Mobile;
    }

    if (TivoSettings.TABLET_REGEX.test(ua)) {
      return DeviceType.Tablet;
    }

    // iPadOS Safari reports itself as Macintosh.
    if (
      navigator.maxTouchPoints > 0 &&
      TivoSettings.MACINTOSH_REGEX.test(ua) &&
      window.screen.width >= 768
    ) {
      return DeviceType.Tablet;
    }

    // Desktop browser detection
    if (/Edg\//i.test(ua)) {
      return DeviceType.Edge;
    }

    if (/OPR\//i.test(ua)) {
      return DeviceType.Opera;
    }

    if (/Firefox\//i.test(ua)) {
      return DeviceType.Firefox;
    }

    if (/Chrome\//i.test(ua)) {
      return DeviceType.Chrome;
    }

    if (/Safari\//i.test(ua)) {
      return DeviceType.Safari;
    }

    return DeviceType.Unknown;
  }

  /**
   * Generate a config filename from the current URL.
   *
   * Old model: msoName → fetch config
   * New model: window.location.hostname + window.location.pathname → fetch config
   *
   * Examples:
   * - clientA.company.com → clientA-company-com-config.json
   * - clientB.company.com/xyz → clientB-company-com-xyz-config.json
   *
   * Falls back to msoName-based config if URL-driven config is not available.
   */
  private getConfigFileNameFromUrl(): string | null {
    try {
      const hostname = window.location.hostname;
      const pathname = window.location.pathname;

      // Skip URL-derived config for localhost or development environments
      if (!hostname || hostname === "localhost" || hostname === "127.0.0.1") {
        return null;
      }

      // Replace dots and slashes with dashes, remove trailing/leading dashes
      let configName = hostname.replace(/\./g, "-");
      if (pathname && pathname !== "/") {
        const cleanPath = pathname.replace(/^\/|\/$/g, "").replace(/\//g, "-");
        if (cleanPath) {
          configName += `-${cleanPath}`;
        }
      }

      return configName;
    } catch {
      return null;
    }
  }

  public getAppName = (): string => {
    // TODO: replace app name from branding ui bundle
    return `${this.getMsoName()}-${this.getDeviceType()}`;
  };

  public getBodyId = async (): Promise<string> => {
    return (await StorageAdapter.getInstance().get(STORAGE_KEY_BODY_ID)) || "";
  };

  /**
   * @returns {string} tsn number from bodyId
   */
  public getDeviceTsn = async (): Promise<string> => {
    const bodyId = await this.getBodyId();
    return bodyId ? bodyId.replace("tsn:", "") : "";
  };

  public getDeviceType = (): string => {
    let deviceType: string;
    switch (this.detectDeviceType()) {
      case DeviceType.TV:
        deviceType = SUPPORTED_DEVICE_TYPES.IPTV_SMART_TV;
        break;
      case DeviceType.Chrome:
      case DeviceType.Edge:
      case DeviceType.Firefox:
      case DeviceType.Opera:
        deviceType = SUPPORTED_DEVICE_TYPES.WEB_PLAYER_CHROME;
        break;
      case DeviceType.Safari:
        deviceType = SUPPORTED_DEVICE_TYPES.WEB_PLAYER_SAFARI;
        break;
      default:
        deviceType =
          this.oTivoSettings && this.oTivoSettings.deviceType
            ? this.oTivoSettings.deviceType
            : SUPPORTED_DEVICE_TYPES.WEB_PLAYER;
    }

    // Allow override of device type via localStorage for testing purposes.
    // This can be removed once all deviceType will be onboarded.
    let overridenDeviceType: string | null = null;
    try {
      overridenDeviceType = localStorage.getItem(
        STORAGE_KEY_OVERRIDE_DEVICE_TYPE
      );
    } catch (error) {
      Log.w(
        TivoSettings.TAG,
        "getDeviceType: Unable to access localStorage for override",
        error
      );
    }

    return overridenDeviceType || deviceType || DEFAULT_DEVICE_TYPE;
  };

  public getDisplayName = (): string => {
    return (
      (this.oTivoSettings && this.oTivoSettings.appName) ||
      DEFAULT_APPLICATION_NAME
    );
  };

  /**
   * Get MSO identifier derived from URL for config resolution.
   * **Source:** window.location.hostname + window.location.pathname
   *
   * This is the new identifier used by Unity Shell to resolve config
   * without MSO-specific coupling.
   */
  public getMsoIdentifierFromUrl = (): string => {
    try {
      const hostname = window.location.hostname;
      const pathname = window.location.pathname;
      let identifier = hostname.replace(/\./g, "-");
      if (pathname && pathname !== "/") {
        const cleanPath = pathname.replace(/^\/|\/$/g, "").replace(/\//g, "-");
        if (cleanPath) {
          identifier += `-${cleanPath}`;
        }
      }
      return identifier;
    } catch {
      return "";
    }
  };

  /**
   * Get MSO name.
   * **Unity Shell HLD:** msoName dependency removed for config resolution.
   * Still available from config for runtime use (API headers, logging).
   *
   * TODO: For new Unity Shell clients, msoName is derived from the config file
   * fetched via URL-driven strategy. The shell no longer provides msoName directly.
   */
  public getMsoName = (): string => {
    const windowExt = window as unknown as IWindowExtended;
    const appInfoVendor =
      windowExt.appInfo !== undefined && windowExt.appInfo !== null
        ? (windowExt.appInfo["vendor"] as string | undefined)
        : undefined;
    // TODO: In case of TMIS shell, we need to implement the logic to fetch its msoName/vendor.
    return (
      (this.oTivoSettings && this.oTivoSettings.msoName) ||
      appInfoVendor ||
      DEFAULT_MSO_NAME
    );
  };

  public getMsoPartnerId = async (): Promise<string> => {
    return (
      (await StorageAdapter.getInstance().get<string>(
        STORAGE_KEY_PARTNER_ID
      )) || ""
    );
  };

  public getMsoRegionName = (): string => {
    return (this.oTivoSettings && this.oTivoSettings.msoRegionName) || "";
  };

  public getPartnerCustomerId = async (): Promise<string> => {
    return (
      (await StorageAdapter.getInstance().get<string>(
        STORAGE_KEY_PARTNER_CUSTOMER_ID
      )) || ""
    );
  };

  /**
   * @returns {object} Player MSO config.
   */
  public getPlayerMsoConfig = (): Record<string, unknown> => {
    const msoOverride = this.oTivoSettings && this.oTivoSettings.msoOverride;
    return (msoOverride && msoOverride.playerMsoConfig) || {};
  };

  public getProvisioningDomain = (): string | undefined => {
    return this.oTivoSettings && this.oTivoSettings.provisioningDomain;
  };

  public getSegmentTransportKey = (): string | undefined => {
    return this.oTivoSettings && this.oTivoSettings.segmentKey;
  };

  public getShellVersion = (): string => {
    // Priority: window.shellAppVersion (shell injection) > shell app postMessage
    return (
      (this.oTivoSettings && this.oTivoSettings.shellVersion) ||
      (window as unknown as IWindowExtended).shellAppVersion ||
      ""
    );
  };

  public getTsriBaseUrl = (): string | undefined => {
    return this.oTivoSettings && this.oTivoSettings.slsEndpointsUrl;
  };

  public getTsriDomain = (): string | undefined => {
    return this.oTivoSettings && this.oTivoSettings.slsDomain;
  };

  public getWebAppUrl = (): string => {
    return (this.oTivoSettings && this.oTivoSettings.webAppUrl) || "";
  };

  /**
   * Initialize TivoSettings.
   *
   * **Value resolution order:**
   * 1. Shell app window object (window.__UNITY_SHELL_BRIDGE__)
   * 2. Shell app postMessage (MessageService)
   * 3. URL-driven config from S3 (fetchAppConfig)
   */
  public initialize = async (
    skipMessageService: boolean = false
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      if (skipMessageService) {
        Log.i(
          TivoSettings.TAG,
          "initialize(): Skipping MessageService, " +
            "using config file fallback."
        );
        this.fetchAppConfig(resolve, reject);
        return;
      }
      new MessageService(MessageType.GET_TIVO_SETTINGS)
        .getResponse()
        .then((response) => {
          Log.i(
            TivoSettings.TAG,
            `initialize(): Got the TiVo Settings ${response}`
          );
          if (
            response !== true &&
            typeof response === "object" &&
            response !== null
          ) {
            this.oTivoSettings = response as ITivoSettings;
          } else {
            this.oTivoSettings = {};
          }
        })
        .catch((error) => {
          Log.e(
            TivoSettings.TAG,
            "initialize(): Failed to initialize TiVo Settings.",
            error
          );
        })
        .finally(() => {
          this.fetchAppConfig(resolve, reject);
        });
    });
  };

  /**
   * check config.json for key browserAllowed to allow app to execute in browser without shell app
   * @returns app allowed to execute in browser without shell app;
   */
  public isBrowserAllowed = (): boolean => {
    return !!(this.oTivoSettings && this.oTivoSettings.browserAllowed);
  };

  /**
   * @returns {boolean} 608 Closed Caption MSO config.
   */
  public isClosedCaption608Allowed = (): boolean => {
    const msoOverride = this.oTivoSettings && this.oTivoSettings.msoOverride;
    const isAllowed = msoOverride
      ? msoOverride.closedCaption608Allowed
      : undefined;
    return isAllowed !== null && isAllowed !== undefined ? isAllowed : true;
  };

  /**
   * @returns {boolean} 708 Closed Caption MSO config.
   */
  public isClosedCaption708Allowed = (): boolean => {
    const msoOverride = this.oTivoSettings && this.oTivoSettings.msoOverride;
    const isAllowed = msoOverride
      ? msoOverride.closedCaption708Allowed
      : undefined;
    return isAllowed !== null && isAllowed !== undefined ? isAllowed : true;
  };

  /**
   * @returns {boolean} Smpte Subtitle MSO config.
   */
  public isSubtitleSmpteAllowed = (): boolean => {
    const msoOverride = this.oTivoSettings && this.oTivoSettings.msoOverride;
    const isAllowed = msoOverride
      ? msoOverride.subtitleSmpteAllowed
      : undefined;
    return isAllowed !== null && isAllowed !== undefined ? isAllowed : true;
  };

  public isValidMsoConfig = (): boolean => {
    return this.mIsValidMsoConfig;
  };

  public setBodyId = async (bodyId: string): Promise<void> => {
    try {
      await StorageAdapter.getInstance().set(
        STORAGE_KEY_BODY_ID,
        bodyId,
        Number.POSITIVE_INFINITY
      );
    } catch (error: unknown) {
      Log.e(TivoSettings.TAG, "setBodyId() storage write failed", error);
    }
  };

  public setMsoPartnerId = async (partnerId: string): Promise<void> => {
    try {
      await StorageAdapter.getInstance().set(
        STORAGE_KEY_PARTNER_ID,
        partnerId,
        Number.POSITIVE_INFINITY
      );
    } catch (error: unknown) {
      Log.e(TivoSettings.TAG, "setMsoPartnerId() storage write failed", error);
    }
  };

  public setPartnerCustomerId = async (
    partnerCustomerId: string
  ): Promise<void> => {
    try {
      await StorageAdapter.getInstance().set(
        STORAGE_KEY_PARTNER_CUSTOMER_ID,
        partnerCustomerId,
        Number.POSITIVE_INFINITY
      );
    } catch (error: unknown) {
      Log.e(
        TivoSettings.TAG,
        "setPartnerCustomerId() storage write failed",
        error
      );
    }
  };

  public setProvisioningDomain = (domain: string): void => {
    this.oTivoSettings.provisioningDomain = domain;
  };
}
